import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, FolderKanban, Users, Handshake, Mail, LogOut, Shield,
  ChevronRight, Sparkles, Archive, Settings,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth, ROLE_LABELS, isAdmin, isStaff, isMemberPlus } from "@/lib/AuthContext";

const KIND_LABEL = {
  progetto: "Progetto",
  evento: "Evento",
  collaborazione: "Collaborazione",
};
const KIND_COLOR = {
  progetto: "bg-[#FF6100]/10 text-[#FF6100]",
  evento: "bg-[#216B65]/10 text-[#216B65]",
  collaborazione: "bg-[#B69838]/15 text-[#7A6820]",
};
const STATUS_LABEL = {
  attivo: "Attivo", concluso: "Concluso", in_pausa: "In pausa",
  iscrizioni_aperte: "Iscrizioni aperte", pubblicato: "Pubblicato", completo: "Completo",
  annullato: "Annullato", attiva: "Attiva", conclusa: "Conclusa",
};

export default function AreaRiservata() {
  const { user, logout, ready } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState({ items: [], years: [] });
  const [yearFilter, setYearFilter] = useState(null);
  const [kindFilter, setKindFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) return;
    if (!user) { nav("/accedi", { replace: true, state: { from: "/area-riservata" } }); return; }
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/area/summary"),
          api.get("/area/timeline"),
        ]);
        setSummary(s.data);
        setTimeline(t.data);
      } catch (e) {
        console.error(e);
      } finally { setLoading(false); }
    })();
  }, [ready, user, nav]);

  const doLogout = () => { logout(); nav("/"); };

  if (!ready || !user) return null;

  const role = user.role;
  const roleLabel = ROLE_LABELS[role] || role;
  const filteredItems = timeline.items.filter((it) =>
    (yearFilter === null || it.year === yearFilter) &&
    (kindFilter === "all" || it.kind === kindFilter),
  );

  return (
    <div data-testid="area-riservata" className="min-h-screen bg-[#FFFCF8]">
      {/* Header */}
      <section className="bg-[#292522] text-[#FFFCF8] pt-14 pb-16">
        <div className="container-vv">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#FF6100]/90 font-semibold">Area riservata</div>
              <h1 className="mt-3 font-display font-medium text-4xl sm:text-5xl">Ciao, {user.first_name || user.name || user.email}</h1>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FF6100]/15 text-[#FF6100] px-3 py-1 text-xs font-semibold">
                <Shield className="h-3.5 w-3.5" strokeWidth={2} /> {roleLabel}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {isAdmin(role) && (
                <Link to="/admin" className="btn-primary text-sm" data-testid="link-to-cms">
                  <Settings className="h-4 w-4" strokeWidth={1.75} /> Pannello CMS
                </Link>
              )}
              <button onClick={doLogout} className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-[#FFFCF8] px-4 py-2 text-sm" data-testid="area-logout">
                <LogOut className="h-4 w-4" strokeWidth={1.75} /> Esci
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard widgets */}
      <section className="container-vv -mt-8">
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <WidgetCard icon={Calendar} label="Le mie prenotazioni" value={summary?.my_bookings ?? "—"} accent="#FF6100" />
          <WidgetCard icon={Sparkles} label="Prossimi eventi" value={summary?.upcoming_events?.length ?? 0} accent="#216B65" />
          {isMemberPlus(role) && (
            <WidgetCard icon={FolderKanban} label="Progetti attivi" value={summary?.projects_active ?? 0} accent="#B69838" />
          )}
          {isMemberPlus(role) && (
            <WidgetCard icon={Archive} label="Progetti totali" value={summary?.projects_total ?? 0} accent="#7A6820" />
          )}
          {isAdmin(role) && (
            <>
              <WidgetCard icon={Users} label="Utenti totali" value={summary?.users_total ?? 0} accent="#FF6100" />
              <WidgetCard icon={Mail} label="Messaggi nuovi" value={summary?.messages_new ?? 0} accent="#216B65" />
            </>
          )}
        </div>
      </section>

      {/* Upcoming events (all roles) */}
      {summary?.upcoming_events?.length > 0 && (
        <section className="container-vv mt-10">
          <h2 className="font-display text-2xl font-medium mb-4">Prossimi appuntamenti</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {summary.upcoming_events.map((e) => (
              <Link key={e.id} to={`/eventi/${e.slug}`} className="card-vv p-5 bg-white hover:border-[#FF6100]/30 transition-colors" data-testid={`upcoming-${e.slug}`}>
                <div className="text-xs text-[#FF6100] font-semibold">{formatDate(e.start_date)}</div>
                <div className="mt-1 font-semibold">{e.title}</div>
                {e.location && <div className="mt-1 text-xs text-[#292522]/60">{e.location}</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Timeline / archivio storico (soci+) */}
      {isMemberPlus(role) ? (
        <section className="container-vv mt-14 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <div className="eyebrow">Riservato ai soci</div>
              <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl">Storia e attività di Vola Vola</h2>
              <p className="mt-3 text-[#292522]/70 max-w-2xl">
                Archivio storico: progetti, eventi e collaborazioni filtrabili per anno. La visibilità di ciascun elemento è impostata dal direttivo.
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} testid="filter-kind-all">Tutto</FilterChip>
            <FilterChip active={kindFilter === "progetto"} onClick={() => setKindFilter("progetto")} testid="filter-kind-progetto">Progetti</FilterChip>
            <FilterChip active={kindFilter === "evento"} onClick={() => setKindFilter("evento")} testid="filter-kind-evento">Eventi</FilterChip>
            <FilterChip active={kindFilter === "collaborazione"} onClick={() => setKindFilter("collaborazione")} testid="filter-kind-collab">Collaborazioni</FilterChip>
          </div>
          {timeline.years.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <FilterChip active={yearFilter === null} onClick={() => setYearFilter(null)} testid="filter-year-all">Tutti gli anni</FilterChip>
              {timeline.years.map((y) => (
                <FilterChip key={y} active={yearFilter === y} onClick={() => setYearFilter(y)} testid={`filter-year-${y}`}>{y}</FilterChip>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-[#292522]/60">Caricamento…</div>
          ) : filteredItems.length === 0 ? (
            <div className="rounded-2xl bg-[#F8F1E8] p-10 text-center text-[#292522]/60">Nessun elemento con questi filtri.</div>
          ) : (
            <div className="space-y-3" data-testid="timeline-list">
              {filteredItems.map((it) => (
                <TimelineRow key={`${it.kind}-${it.id}`} item={it} />
              ))}
            </div>
          )}
        </section>
      ) : (
        // Utente semplice: promo per diventare socio
        <section className="container-vv mt-14 pb-16">
          <div className="rounded-3xl bg-[#FFF1E8] border border-[#FF6100]/20 p-8 lg:p-12">
            <div className="eyebrow">Vuoi vedere di più?</div>
            <h2 className="mt-3 font-display font-medium text-3xl">Diventa socio/a</h2>
            <p className="mt-3 text-[#292522]/75 max-w-2xl">
              Come socio/a di Vola Vola avrai accesso all'archivio storico completo dell'associazione, ai verbali delle assemblee, alle convocazioni e ai materiali riservati.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/entra-nella-comunita" className="btn-primary text-sm">Come diventare socio</Link>
              <Link to="/contatti" className="btn-ghost text-sm">Scrivici</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function WidgetCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-vv p-5 bg-white">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${accent}18`, color: accent }}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <div className="text-2xl font-display font-medium">{value}</div>
      <div className="text-xs text-[#292522]/60 mt-1">{label}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#FF6100] text-white" : "bg-[#F8F1E8] text-[#292522] hover:bg-[#FFF1E8]"}`}
    >
      {children}
    </button>
  );
}

function TimelineRow({ item }) {
  const kindClass = KIND_COLOR[item.kind] || "bg-[#F8F1E8] text-[#292522]";
  const kindText = KIND_LABEL[item.kind] || item.kind;
  return (
    <div className="card-vv p-5 bg-white flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {item.cover_image ? (
          <img src={fileUrl(item.cover_image)} alt={item.title} className="h-14 w-14 rounded-xl object-cover shrink-0" />
        ) : item.logo ? (
          <img src={fileUrl(item.logo)} alt={item.title} className="h-14 w-14 rounded-xl object-contain bg-[#FFF1E8] p-2 shrink-0" />
        ) : (
          <div className="h-14 w-14 rounded-xl bg-[#FFF1E8] shrink-0 flex items-center justify-center text-[#FF6100] font-display font-medium">{(item.title || "?")[0]}</div>
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2 items-center">
            <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ${kindClass}`}>{kindText}</span>
            {item.year && <span className="text-xs text-[#292522]/60">{item.year}{item.year_ended && item.year_ended !== item.year ? `–${item.year_ended}` : ""}</span>}
            {item.status && <span className="text-xs text-[#292522]/60">· {STATUS_LABEL[item.status] || item.status}</span>}
          </div>
          <div className="font-semibold mt-1 truncate">{item.title}</div>
          {item.short_desc && <div className="text-xs text-[#292522]/60 mt-1 line-clamp-2">{item.short_desc}</div>}
        </div>
      </div>
      {item.slug && (
        <Link to={item.kind === "progetto" ? `/progetti/${item.slug}` : `/eventi/${item.slug}`} className="text-sm text-[#FF6100] inline-flex items-center gap-1 shrink-0">
          Apri <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
